/**
 * One image slot of the Pass Designer Images section: three resolution tiles
 * (@1x / @2x / @3x). Each tile, on file pick:
 *   1. requestDesignImageUploadUrl({ slot, scale, contentType: 'image/png',
 *      contentLength: file.size }) → { uploadUrl, s3KeyBase, s3Key }
 *      (POST /v1/portal/design-images — the presign is signed to that exact
 *      size, capped at 1 MiB; oversize files are rejected client-side first).
 *   2. PUT the PNG bytes directly to uploadUrl (Content-Type: image/png).
 * On the @2x upload (the canonical base) it sets images.<slot>.s3KeyBase to the
 * returned s3KeyBase; @1x/@3x land on the sibling suffixes of that same base.
 * @2x-only is accepted (bundled-default fallback at mint) — the operator is not
 * forced to upload all three.
 */

import type { DeepPartial, PassDesign } from '@insurwiz/wallet-core';
import { useState } from 'react';

import { ApiClientError, requestDesignImageUploadUrl } from '../../api/client';
import type { DesignImageScale } from '../../types';

import type { ImageSlot } from './styleSlots';

// Actionable failure copy (was the bare "Feltöltés sikertelen" — matched Adam's
// "csak azt kaptam hogy sikertelen" with no server log to chase, since the pass
// bucket had no CORS rule and the preflight died client-side). The HTTP status
// is appended when known (a non-OK PUT response, or an ApiClientError from the
// presign request) — a network-level throw (no response at all) omits it.
function uploadFailedMessage(status: number | undefined): string {
  const base = 'A feltöltés nem ment át — frissítsd az oldalt és próbáld újra.';
  return status === undefined ? base : `${base} (HTTP ${status})`;
}

const SCALES: ReadonlyArray<{ scale: DesignImageScale; label: string }> = [
  { scale: '', label: '@1x' },
  { scale: '@2x', label: '@2x' },
  { scale: '@3x', label: '@3x' },
];

// Client-side mirror of the server presign cap (designImageUploader MAX_BYTES):
// reject oversize files here with a friendly message instead of a 413 round-trip.
const MAX_IMAGE_BYTES = 1024 * 1024;

export interface ImageSlotUploaderProps {
  slot: ImageSlot;
  /** Current s3KeyBase for this slot, if any. */
  s3KeyBase: string | undefined;
  disabled: boolean;
  /** Tooltip/explanation shown when `disabled` (e.g. eventTicket strip-XOR). */
  disabledReason?: string;
  onChange: (patch: DeepPartial<PassDesign>) => void;
}

// Hungarian display name for each image slot, used for BOTH the visible tile
// label AND the per-resolution file-input aria-label. The underlying slot KEYS
// (icon/logo/strip/… — the PassDesign.images keys) stay untouched; only these
// operator-facing labels are localized. Never build an aria-label from the raw
// slot key ("strip @2x") — always from this map.
const SLOT_LABELS: Record<ImageSlot, string> = {
  icon: 'Ikon (kötelező)',
  logo: 'Logó',
  strip: 'Csík',
  thumbnail: 'Bélyegkép',
  background: 'Háttérkép',
  footer: 'Lábléc',
};

export function ImageSlotUploader({
  slot,
  s3KeyBase,
  disabled,
  disabledReason,
  onChange,
}: ImageSlotUploaderProps): JSX.Element {
  const [busyScale, setBusyScale] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function upload(scale: DesignImageScale, file: File): Promise<void> {
    if (file.size > MAX_IMAGE_BYTES) {
      setError('A kép max 1 MB lehet.');
      return;
    }
    setBusyScale(scale);
    setError(null);
    try {
      const { uploadUrl, s3KeyBase: base } = await requestDesignImageUploadUrl({
        slot,
        scale,
        contentType: 'image/png',
        contentLength: file.size,
      });
      const res = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'content-type': 'image/png' },
        body: file,
      });
      if (!res.ok) {
        setError(uploadFailedMessage(res.status));
        return;
      }
      // The @2x asset is canonical: its returned base is what the design stores
      // and what ApplePassStore.getImage resolves @1x/@3x siblings from.
      if (scale === '@2x') {
        onChange({ images: { [slot]: { s3KeyBase: base } } } as DeepPartial<PassDesign>);
      }
    } catch (e) {
      setError(uploadFailedMessage(e instanceof ApiClientError ? e.status : undefined));
    } finally {
      setBusyScale(null);
    }
  }

  return (
    <div className="rounded-md ring-1 ring-slate-200 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-slate-700">{SLOT_LABELS[slot]}</span>
        {s3KeyBase ? (
          <span className="text-[11px] text-slate-400" title={s3KeyBase}>
            feltöltve
          </span>
        ) : null}
      </div>
      {disabled ? (
        <p className="text-xs text-amber-700">
          {disabledReason ?? 'Ehhez a stílushoz nem elérhető'}
        </p>
      ) : (
        <div className="flex gap-2">
          {SCALES.map(({ scale, label }) => (
            <label
              key={label}
              className="flex-1 cursor-pointer rounded ring-1 ring-slate-300 px-2 py-1 text-center text-xs text-slate-600 hover:bg-slate-50"
            >
              {busyScale === scale ? '…' : label}
              <input
                type="file"
                accept="image/png"
                aria-label={`${SLOT_LABELS[slot]} ${label}`}
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void upload(scale, file);
                }}
              />
            </label>
          ))}
        </div>
      )}
      {error ? <p className="text-xs text-red-600">{error}</p> : null}
    </div>
  );
}
